import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Barcode, Save, Loader2, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { systemSetupSsccService } from '../../../../../services/systemSetupSsccService';
import { UpdateSystemSetupSSCCRequest } from '../../../../../types/systemSetupSscc';

interface SsccSettingsFormProps {
  refreshTrigger?: number;
  onBack?: () => void;
}

const EMPTY_FORM: UpdateSystemSetupSSCCRequest = {
  ssccCompanyPrefix: '',
  ssccExtDigit: 0,
  ssccSerialReference: 0,
  grCreateBatchID: false,
  grBatchIDLength: 0,
  grBatchIDValue: 0,
};

/**
 * Legger på et mod-10 sjekksiffer (GS1) på en sifferstreng. Kun for forhåndsvisning;
 * den autoritative beregningen skjer i backend (ISsccService.AddCheckMod10).
 */
function addCheckMod10(value: string): string {
  if (!value || !/^\d+$/.test(value)) return value;
  let multi = 1;
  let sum = 0;
  for (let i = value.length - 1; i >= 0; i--) {
    multi = multi === 1 ? 3 : 1;
    sum += (value.charCodeAt(i) - 48) * multi;
  }
  const check = (10 - (sum % 10)) % 10;
  return value + check.toString();
}

export const SsccSettingsForm: React.FC<SsccSettingsFormProps> = ({ refreshTrigger, onBack }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<UpdateSystemSetupSSCCRequest>(EMPTY_FORM);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [configured, setConfigured] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const current = await systemSetupSsccService.getCurrent();
      if (current) {
        setConfigured(true);
        setForm({
          ssccCompanyPrefix: current.ssccCompanyPrefix ?? '',
          ssccExtDigit: current.ssccExtDigit ?? 0,
          ssccSerialReference: current.ssccSerialReference ?? 0,
          grCreateBatchID: current.grCreateBatchID ?? false,
          grBatchIDLength: current.grBatchIDLength ?? 0,
          grBatchIDValue: current.grBatchIDValue ?? 0,
        });
      } else {
        setConfigured(false);
        setForm(EMPTY_FORM);
      }
    } catch (err: any) {
      setError(err.message || t('manager.modules.adminHost.ssccConfig.loadError', {}, 'Kunne ikke laste SSCC-konfigurasjon'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, refreshTrigger]);

  // Live-forhåndsvisning av neste SSCC (slik backend vil bygge den).
  const preview = useMemo(() => {
    const rawPrefix = (form.ssccCompanyPrefix || '').trim();
    if (!rawPrefix || !/^\d+$/.test(rawPrefix)) return null;
    const prefix = rawPrefix.length < 7 ? rawPrefix.padStart(7, '0') : rawPrefix;
    const serialLen = Math.max(0, 16 - prefix.length);
    // Neste genererte serienummer = nåværende + 1 (matcher backend-inkrement).
    const nextSerial = (form.ssccSerialReference + 1).toString().padStart(serialLen, '0');
    const body = `${form.ssccExtDigit}${prefix}${nextSerial}`;
    if (body.length !== 17) return null;
    return addCheckMod10(body);
  }, [form.ssccCompanyPrefix, form.ssccExtDigit, form.ssccSerialReference]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await systemSetupSsccService.save(form);
      setConfigured(true);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || t('manager.modules.adminHost.ssccConfig.saveError', {}, 'Kunne ikke lagre SSCC-konfigurasjon'));
    } finally {
      setSaving(false);
    }
  };

  const numField = (val: number, onChange: (n: number) => void, min?: number, max?: number) => (
    <input
      type="number"
      value={val}
      min={min}
      max={max}
      onChange={(e) => {
        let n = parseInt(e.target.value, 10);
        if (isNaN(n)) n = 0;
        if (min !== undefined && n < min) n = min;
        if (max !== undefined && n > max) n = max;
        onChange(n);
      }}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        {t('common.loading', {}, 'Laster...')}
      </div>
    );
  }

  return (
    <div>
      {onBack && (
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back', {}, 'Tilbake')}
        </button>
      )}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-blue-100 p-2 rounded-lg">
          <Barcode className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t('manager.modules.adminHost.ssccConfig.title', {}, 'SSCC-konfigurasjon')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('manager.modules.adminHost.ssccConfig.description', {}, 'Styrer hvordan pall-ID (SSCC) bygges opp ved oppretting av paller i plukk.')}
          </p>
        </div>
      </div>

      {!configured && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 my-4 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{t('manager.modules.adminHost.ssccConfig.notConfigured', {}, 'SSCC er ikke konfigurert for dette firmaet ennå. Paller får en midlertidig ID (PALL-…) inntil du lagrer en konfigurasjon her.')}</span>
        </div>
      )}

      {/* SSCC-oppbygging */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('manager.modules.adminHost.ssccConfig.fields.extDigit', {}, 'Extension digit')}
          </label>
          {numField(form.ssccExtDigit, (n) => setForm(f => ({ ...f, ssccExtDigit: n })), 0, 9)}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('manager.modules.adminHost.ssccConfig.fields.companyPrefix', {}, 'Company prefix')}
          </label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={10}
            value={form.ssccCompanyPrefix}
            onChange={(e) => setForm(f => ({ ...f, ssccCompanyPrefix: e.target.value.replace(/[^0-9]/g, '') }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('manager.modules.adminHost.ssccConfig.fields.serialNumber', {}, 'Serienummer (teller)')}
          </label>
          {numField(form.ssccSerialReference, (n) => setForm(f => ({ ...f, ssccSerialReference: n })), 0)}
        </div>
      </div>

      {/* Forhåndsvisning */}
      <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
          {t('manager.modules.adminHost.ssccConfig.previewLabel', {}, 'Neste SSCC (forhåndsvisning)')}
        </div>
        <div className="font-mono text-lg text-gray-900">
          {preview || t('manager.modules.adminHost.ssccConfig.previewInvalid', {}, '— (fyll inn gyldig prefiks)')}
        </div>
      </div>

      {/* Batch ved varemottak */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <label className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            checked={form.grCreateBatchID}
            onChange={(e) => setForm(f => ({ ...f, grCreateBatchID: e.target.checked }))}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            {t('manager.modules.adminHost.ssccConfig.fields.grCreateBatch', {}, 'Auto-generer batchnummer ved varemottak')}
          </span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manager.modules.adminHost.ssccConfig.fields.grBatchLength', {}, 'Batchnummer lengde')}
            </label>
            {numField(form.grBatchIDLength, (n) => setForm(f => ({ ...f, grBatchIDLength: n })), 0)}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manager.modules.adminHost.ssccConfig.fields.grBatchValue', {}, 'Batchnummer verdi')}
            </label>
            {numField(form.grBatchIDValue, (n) => setForm(f => ({ ...f, grBatchIDValue: n })), 0)}
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mt-4 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('common.save', {}, 'Lagre')}
        </button>
        {success && (
          <span className="inline-flex items-center gap-1 text-green-600 text-sm">
            <CheckCircle2 className="w-4 h-4" />
            {t('common.saved', {}, 'Lagret')}
          </span>
        )}
      </div>
      </div>
    </div>
  );
};
